import type { JSX } from 'react';

import { Redirect } from '@change-corgi/core/react/router';
import { Container } from '@change-corgi/design-system-legacy/layout';
import { Heading } from '@change-corgi/design-system-legacy/typography';

import type { PrefetchedDataProps } from 'src/shared/prefetch';
import { withPrefetchedData } from 'src/shared/prefetch';

import { isError, isLoading } from 'src/app/shared/utils/async';

import { prefetchPetitionInfo, usePetitionInfo } from '../../legacy/petitionInfo';
import type { PetitionInfoState } from '../../legacy/petitionInfo';
import type { UserInfoState } from '../../legacy/types';

import { BannerContainer } from './BannerContainer';

type Props = Readonly<{
	slug: string;
	userInfoState: UserInfoState;
	hideTitle?: boolean;
}>;
type PrefetchedData = { info: PetitionInfoState };

export const PetitionHeaderContainer = withPrefetchedData(
	({
		slug,
		prefetchedData,
		userInfoState,
		hideTitle,
	}: Props & PrefetchedDataProps<PrefetchedData>): JSX.Element | null => {
		const petitionInfoState = usePetitionInfo(slug, prefetchedData?.info);

		// TODO use a fixed height or a ghost to avoid layout shifting?
		if (isLoading(petitionInfoState) || isLoading(userInfoState)) return null;

		if (isError(petitionInfoState) && petitionInfoState.reason === 'notFound') {
			return <Redirect to="/?petition_not_found=true" />;
		}

		if (isError(petitionInfoState)) {
			throw new Error('Petition Header: Failed to load petition details', { cause: petitionInfoState.cause });
		}
		if (isError(userInfoState)) {
			throw new Error('Petition Header: Failed to load user info', { cause: userInfoState.cause });
		}

		const { ask: title } = petitionInfoState.info;
		const { info: userInfo } = userInfoState;

		return (
			<>
				<BannerContainer
					petitionId={petitionInfoState.info.id}
					emergencyBanner={petitionInfoState.info.emergencyBanner}
					signed={userInfo.signed}
					isOwner={userInfo.ownPetition}
				/>

				{hideTitle ? null : (
					<Container px={8}>
						<Heading as="h1" size={['h4', 'h3']} mt={16}>
							{title}
						</Heading>
					</Container>
				)}
			</>
		);
	},
	{
		prefetchName: __MODULE_HASH__,
		prefetchDependencies: [BannerContainer],
		prefetchData: async (context) => {
			const {
				params: { slug },
				utilityContext,
			} = context;
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			return { info: await prefetchPetitionInfo(slug!, utilityContext) };
		},
	},
);
